import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Users, MapPin, ArrowLeft, ChevronDown, ChevronRight, Star, Globe } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "../i18n";
import { useTheme } from "../theme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegionsPage() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [expandedCountry, setExpandedCountry] = useState(null);
  const [expandedCluster, setExpandedCluster] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [appSettings, setAppSettings] = useState(null);

  useEffect(() => {
    fetchRegions();
    axios.get(`${API}/settings/public`).then(r => setAppSettings(r.data)).catch(() => {});
  }, []);

  const fetchRegions = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/regions`);
      setData(res.data);
      // Auto-expand first country
      if (res.data.regions?.length > 0) {
        setExpandedCountry(res.data.regions[0].country);
      }
    } catch {
      toast.error("Failed to load regional data");
    }
    setIsLoading(false);
  };

  const labelMap = {};
  if (data?.labels) {
    data.labels.forEach((lb) => { labelMap[lb.id] = lb; });
  }

  return (
    <div className="min-h-screen h-screen overflow-y-auto" style={{ background: isDark ? "#0f172a" : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #fce7f3 100%)" }} data-testid="regions-page">
      <nav className="nav-bar" data-testid="regions-nav">
        <Link to="/" className="nav-logo">
          <img src={`${API}/logo`} alt="" className="w-10 h-10 rounded-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
          <span>{appSettings?.app_name || t("app_name")}</span>
        </Link>
        <h2 className="text-lg font-bold text-slate-700" style={{ fontFamily: "Nunito" }}>
          Regional Communities
        </h2>
        <Link to="/" className="btn-secondary text-sm py-2 px-4" data-testid="back-to-map-regions">
          <ArrowLeft className="w-4 h-4" />
          {t("back_to_map")}
        </Link>
      </nav>

      <main className="max-w-4xl mx-auto p-4 md:p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-slate-800 mb-2" style={{ fontFamily: "Nunito" }} data-testid="regions-heading">
            Regional Community Groups
          </h1>
          <p className="text-slate-500 max-w-lg mx-auto">
            Nearby areas are grouped into regional clusters to help you find and build local communities.
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto" />
          </div>
        ) : !data?.regions?.length ? (
          <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-slate-100">
            <Globe className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No regional data yet. Add pins to the map to form communities!</p>
            <Link to="/" className="btn-primary text-sm mt-4 inline-flex" data-testid="go-to-map-btn">
              <MapPin className="w-4 h-4" />
              Go to Map
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {data.regions.map((region) => (
              <div key={region.country} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden" data-testid={`region-${region.country}`}>
                {/* Country Header */}
                <button
                  onClick={() => setExpandedCountry(expandedCountry === region.country ? null : region.country)}
                  className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors"
                  data-testid={`expand-country-${region.country}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                      <Globe className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: "Nunito" }}>{region.country}</h3>
                      <p className="text-sm text-slate-500">
                        {region.total_pins} {t("people")} across {region.clusters.length} {region.clusters.length === 1 ? "area" : "areas"}
                        {region.event_count > 0 && ` • ${region.event_count} events`}
                      </p>
                    </div>
                  </div>
                  {expandedCountry === region.country ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                </button>

                {/* Clusters */}
                {expandedCountry === region.country && (
                  <div className="border-t border-slate-100 p-4 space-y-3" data-testid={`clusters-${region.country}`}>
                    {region.clusters.map((cluster, idx) => (
                      <div
                        key={idx}
                        className="rounded-xl border border-slate-100 overflow-hidden"
                        data-testid={`cluster-${cluster.name}`}
                      >
                        <button
                          onClick={() => setExpandedCluster(expandedCluster === `${region.country}-${idx}` ? null : `${region.country}-${idx}`)}
                          className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                              <Users className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div className="text-left">
                              <h4 className="font-bold text-slate-800" style={{ fontFamily: "Nunito" }}>
                                {cluster.name}
                              </h4>
                              <p className="text-xs text-slate-500">
                                {cluster.total_pins} {t("people")}
                                {cluster.area_count > 1 && ` • ${cluster.area_count} nearby areas`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Label dots */}
                            <div className="flex gap-1">
                              {Object.keys(cluster.label_counts || {}).slice(0, 5).map((lid) => (
                                <div
                                  key={lid}
                                  className="w-4 h-4 rounded-full border border-white shadow-sm"
                                  style={{ background: labelMap[lid]?.color || "#6B7280" }}
                                  title={`${labelMap[lid]?.name || lid}: ${cluster.label_counts[lid]}`}
                                />
                              ))}
                            </div>
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedCluster === `${region.country}-${idx}` ? "rotate-180" : ""}`} />
                          </div>
                        </button>

                        {expandedCluster === `${region.country}-${idx}` && (
                          <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-3">
                            {/* Label breakdown */}
                            <div>
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Community Breakdown</p>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(cluster.label_counts || {}).map(([lid, count]) => (
                                  <div key={lid} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 shadow-sm">
                                    <div className="w-3 h-3 rounded-full" style={{ background: labelMap[lid]?.color || "#6B7280" }} />
                                    <span className="text-xs font-medium text-slate-700">{labelMap[lid]?.name || lid}</span>
                                    <span className="text-xs font-bold text-slate-500">{count}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Areas list */}
                            {cluster.area_count > 1 && (
                              <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Areas in this region</p>
                                <div className="flex flex-wrap gap-2">
                                  {cluster.areas.map((area, ai) => (
                                    <Link
                                      key={ai}
                                      to={`/?town=${encodeURIComponent(area.town)}&country=${encodeURIComponent(area.country)}`}
                                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-sm text-slate-700 hover:border-indigo-300 hover:text-indigo-700 transition-colors"
                                    >
                                      <MapPin className="w-3 h-3" />
                                      {area.town}
                                      <span className="text-xs text-slate-400">({area.total_pins})</span>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            )}

                            <Link
                              to={`/?town=${encodeURIComponent(cluster.areas[0]?.town)}&country=${encodeURIComponent(cluster.areas[0]?.country)}`}
                              className="btn-primary text-sm inline-flex mt-2"
                              data-testid={`view-on-map-${cluster.name}`}
                            >
                              <MapPin className="w-4 h-4" />
                              View on Map
                            </Link>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
